import clsx from 'clsx';
import React, { ReactNode, useRef, useState } from 'react';
import { useClickOutside } from '../../utils/clickOutSide';
import ChevronDown from '../../assets/icons/select-chevron-down.svg?react';
import WarningIcon from './../../assets/icons/warning-icon.svg?react';

interface SelectProps {
  otherClasses?: {
    container?: string;
    label?: string;
    button?: string;
    listContainer?: string;
    list?: string;
  };
  placeholder?: string;
  value: string;
  label?: string | ReactNode;
  list: {
    label: string;
    value: string;
  }[];
  onSelect: (e: string) => void;
  disabled?: boolean;
  filterSelect?: boolean;
  errorMessage?: string;
}

const Select: React.FC<SelectProps> = ({
  otherClasses,
  placeholder,
  value,
  list,
  onSelect,
  label,
  disabled = false,
  errorMessage,
  filterSelect = false,
}) => {
  const [toggle, setToggle] = useState<boolean>(false);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });

  const selectClasses = clsx(
    otherClasses?.container,
    'flex flex-col gap-2.5 items-start w-fit relative',
  );
  const labelClasses = clsx(
    otherClasses?.label,
    'text-form-label -tracking-form-label font-inter-variable font-bold capitalize',
    disabled ? `text-neutral-tones-text/35` : `text-neutral-tones-text `,
  );
  const buttonClasses = clsx(
    otherClasses?.button,
    'w-full text-left global-transition ring border-transparent flex items-center gap-2 justify-between font-normal font-inter-variable  disabled:ring-neutral-tones-text/35 disabled:text-neutral-tones-text/35 disabled:cursor-not-allowed cursor-pointer capitalize',
    errorMessage !== ''
      ? `ring-brand-posh-pink ring-2`
      : toggle
        ? `ring-brand-byzantine ring-2`
        : filterSelect
          ? 'ring-neutral-tones-text/10'
          : `${value === '' ? `ring-neutral-tones-text/50 ` : `ring-neutral-tones-text`}`,
    filterSelect
      ? 'text-neutral-tones-text'
      : value === ''
        ? 'text-neutral-tones-text/35'
        : 'text-neutral-tones-text',
    filterSelect
      ? 'px-2.5 py-3 lg:py-15 bg-card-backgrounds/5 text-sm rounded-md lg:text-base lg:-tracking-form-input'
      : 'px-5 py-15 bg-transparent text-base -tracking-form-input  rounded-10 ',
  );
  const listContainerClasses = clsx(
    otherClasses?.listContainer,
    'bg-light-translucent-background dark:bg-dark-translucent-background rounded-10 ring-neutral-tones-text/35 ring text-left py-15 px-5 absolute z-10  w-full max-h-64 overflow-auto global-scrollbar-style',
    label ? 'top-24' : 'top-18',
  );
  const listClasses = clsx(
    otherClasses?.list,
    'border-b border-b-neutral-tones-text/10 first:!pt-0 py-2.5 last:pb-0 last:border-0 last:pt-2.5 cursor-pointer',
  );

  return (
    <div ref={ref} className={selectClasses} data-testid="select">
      {label && (
        <label
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) {
              setToggle(!toggle);
            }
          }}
          className={labelClasses}
        >
          {label}
        </label>
      )}
      <button
        title={
          value !== ''
            ? list?.find((node) => node?.value === value)?.label
            : placeholder
        }
        disabled={disabled}
        className={buttonClasses}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setToggle(!toggle);
        }}
      >
        <span className="truncate whitespace-nowrap">
          {value !== ''
            ? list?.find((node) => node?.value === value)?.label.replace('_', ' ')
            : placeholder}
        </span>
        <ChevronDown
          className={clsx(
            'global-transition',
            errorMessage !== ''
              ? `[&>path]:fill-brand-posh-pink`
              : disabled
                ? '[&>path]:fill-neutral-tones-text/35'
                : '[&>path]:fill-neutral-tones-text',
            toggle ? 'rotate-180' : 'rotate-0',
            filterSelect && 'h-5 w-5',
          )}
        />
      </button>
      {toggle && (
        <>
          {list?.length === 0 ? (
            <ul className={listContainerClasses}>
              <li
                className={
                  'border-b-neutral-tones-text/10 cursor-pointer border-b py-2.5 first:pt-0 last:border-0 last:pb-0'
                }
              >
                <button
                  type="button"
                  className={clsx(
                    'font-inter-variable -tracking-form-input text-neutral-tones-text flex w-full cursor-pointer items-center gap-2 text-left text-base font-normal capitalize',
                  )}
                >
                  <WarningIcon className="[&>g>path]:fill-brand-byzantine h-6 w-6 lg:h-6 lg:w-6" />
                  No Data
                </button>
              </li>
            </ul>
          ) : (
            <ul className={listContainerClasses}>
              {list?.map((node, index) => (
                <li key={index} className={listClasses}>
                  <button
                    type="button"
                    className={clsx(
                      'font-inter-variable w-full cursor-pointer text-left font-normal capitalize',
                      node?.value === value
                        ? 'text-brand-byzantine'
                        : 'hover:text-neutral-tones-text text-neutral-tones-text/80',
                      filterSelect
                        ? 'lg:-tracking-form-input text-sm lg:text-base'
                        : '-tracking-form-input text-base',
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelect(node?.value);
                      setToggle(false);
                    }}
                  >
                    {node?.label.replace('_', ' ')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {errorMessage !== '' && (
        <span className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default Select;
